import { ApplicationElement, html } from '../application_element/element.js';
import style from './style.lit.css';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';
import store from 'store2';
import translations from './translations.js';
import { chatConfig } from '../chat/lib/config.js';
import { ResizeController } from '@lit-labs/observers/resize-controller.js';
import escape from 'lodash-es/escape.js';
import unescape from 'lodash-es/unescape.js';

import { htmlToText } from '../../lib/helpers.js';

const { icons } = chatConfig;

/**
 * Chat form - used for sending chat messages
 *
 * Priorities of value:
 *   1. Draft value from local store
 *   2. Value from slotted textarea
 *   3. Value from `value` property
 *
 * @prop chatId - An ID of chat
 * @prop isEdit - Is the user editing an old message ? (default: false)
 * @prop allowedFiles - An array of allowed MIME types for eg. "image/*, application/pdf..."
 * @prop theme - Forms theme default: primary (cross-like), secondary (beUnity-like) (options: primary, secondary )
 * @cssprop [--chat-bg=var(--contrast-light)] Background color.
 * @cssprop [--chat-focused-bg=var(--yellow-100)] Background color while typing.
 * @cssprop [--send-button-bg=var(--primary) for primary theme | transparent for secondary theme] Send button's background.
 * @cssprop [--send-button-color=var(--contrast-light) for primary theme | var(--primary) for secondary theme] Send button's background.
 */
export default class ChatForm extends ApplicationElement {
  /** @internal */
  static translations = translations;

  static styles = [ApplicationElement.styles, style];

  static properties = {
    chatId: { type: Number },
    allowedFiles: { type: String },
    theme: { type: String },
    value: { type: String },
    isEdit: { type: Boolean },
    isMobileDevice: { type: Boolean },
    files: { type: Array, state: true },
    error: { type: String, state: true },
    slottedTextarea: { type: Object, state: true },
  };

  static currentUser;

  resizeObserver = new ResizeController(this, {
    callback: () => this.setTextareaHeight(),
  });

  constructor() {
    super();
    this.files = [];
  }

  connectedCallback() {
    super.connectedCallback();
    this.store = store.namespace(this.chatStoreKey);

    // Because storeKey is available only after connectedCallback only than we can set `value`
    this.initialValue = this.value;
    this.initialDraftValue = this.readStore();
    this.value ||= this.initialDraftValue || '';
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('slottedTextarea')) {
      this.updateTextAreaValue(true);
    }

    if (changedProperties.has('value')) {
      this.updateTextAreaValue();
      this.updateLocalStoreValue();
    }
  }

  updated(changedProperties) {
    changedProperties.has('isEdit') && this.isEdit && this.enterEditMode();

    if (changedProperties.has('value')) {
      // Wait for next frame to get correct textarea height
      requestAnimationFrame(() => this.setTextareaHeight());

      const isFirstUpdate = changedProperties.get('value') === undefined;

      // Only on first update with value
      if (isFirstUpdate) {
        // Defer execution with setTimeout to make sure that the textarea is ready
        setTimeout(() => this.focusTextareaOnDesktop(), 200);
      }
    }
  }

  updateLocalStoreValue() {
    this.value ? this.saveStore(this.value) : this.store.remove('draft');
  }

  updateTextAreaValue(initial = false) {
    const { textarea } = this;
    if (!textarea) return;

    if (!initial) {
      if (textarea.value !== this.value) textarea.value = this.value;
      return;
    }

    // Initial update ------------------------
    if (this.initialValue && textarea.value) {
      throw new Error(
        'Element value and slotted value are both set. Use only one!'
      );
    }

    // Workaround for RTE only for initial load
    // Trix-Editor has slight delay on initialize so textarea value is blank
    const rteTextarea = this.slottedTextarea?.querySelector('textarea');
    const textAreaValue = rteTextarea?.value || textarea.value;

    if (textAreaValue && !this.initialDraftValue) {
      this.value = textAreaValue;
    } else if (this.value) {
      textarea.value = this.value;
    }
  }

  readStore() {
    return this.store.get('draft');
  }

  saveStore(value) {
    this.store.set('draft', value);
  }

  get isRichText() {
    return this.textarea?.tagName.includes('-RTE');
  }

  /**
   * Will return value with escaped html tags
   */
  get safeValue() {
    const { value } = this;
    return this.isRichText ? value : escape(value);
  }

  /**
   * Will set value with unescaped html tags
   */
  set safeValue(value) {
    this.value = this.isRichText ? value : unescape(value);
  }

  /**@internal */
  get chatStoreKey() {
    return `chat-${this.chatId}`;
  }

  /**@internal */
  get canSubmit() {
    return Boolean(this.plainValue.length || this.hasFiles);
  }

  /**
   * Will return plain text without html tags
   * @internal
   */
  get plainValue() {
    return this.isRichText ? htmlToText(this.value).trim() : this.value.trim();
  }

  /**@internal */
  get textarea() {
    return this.slottedTextarea || this.selfTextarea;
  }

  /**@internal */
  get selfTextarea() {
    return this.shadowRoot?.querySelector('textarea');
  }

  /**@internal */
  get form() {
    return this.shadowRoot.querySelector('form');
  }

  get submitButton() {
    return this.form.querySelector('[type="submit"]');
  }

  get hasFiles() {
    return this.files.length > 0;
  }

  onRemoveFile({ currentTarget }) {
    this.files = this.files.filter(
      (file, i) => i != currentTarget.dataset.index
    );
  }

  setTextareaHeight() {
    const { textarea } = this;

    if (!textarea) return;
    if (textarea.tagName !== 'TEXTAREA') return;

    this.textareaClone ??= textarea.cloneNode();

    const { textareaClone } = this;

    textareaClone.style.width = `${textarea.clientWidth}px`;
    textareaClone.tabindex = -1;
    textareaClone.disabled = true;

    // First time
    if (!textareaClone.baseScrollHeight) {
      const cloneWrapper = document.createElement('div');
      cloneWrapper.className = 'textarea-clone-wrapper';

      cloneWrapper.appendChild(textareaClone);
      this.textarea.parentElement.appendChild(cloneWrapper);

      textareaClone.baseScrollHeight = textarea.scrollHeight;
    }

    this.textareaClone.value = textarea.value;
    const rows = Math.floor(
      textareaClone.scrollHeight / this.textareaClone.baseScrollHeight
    );

    this.textarea.rows = Math.min(6, rows);
  }

  enterEditMode() {
    const { textarea } = this;
    textarea.focus();

    if (!this.isRichText) {
      // Scroll to bottom
      textarea.scrollTop = textarea.scrollHeight;
    }
    setTimeout(() => {
      const len = this.value.length;
      textarea.setSelectionRange(len, len);
    }, 0);
  }

  exitEditMode() {
    this.value = '';
    this.isEdit = false;
    this.dispatchEvent(
      new Event('chat-form-exit-edit-mode', { bubbles: true, composed: true })
    );
  }

  onTextInput({ target: textarea }) {
    this.value = textarea.value;
    if (this.isEdit && !this.value) this.exitEditMode();
  }

  onFileInput({ target: { files } }) {
    // Convert to array
    this.files = [...files];
  }

  onSubmit(e) {
    e.preventDefault();
    if (!this.canSubmit) return;

    this.dispatchEvent(new Event('message-create', { bubbles: true }));
    this.cleanUpAfterSubmit();
  }

  onTextareaKeyDown(e) {
    const { key, shiftKey, isComposing } = e;

    // With the German keyboard event is fired twice upon ^ character as keyboard is waiting for another character to possibly display a special character.
    // If isComposing is true, it means that the event is not a final input and should be ignored.
    if (isComposing) return;

    if (!this.isMobileDevice && key === 'Enter' && !shiftKey) {
      e.preventDefault();
      this.form.requestSubmit(this.submitButton);
    }
  }

  cleanUpAfterSubmit() {
    this.value = '';
    this.files = [];
    this.exitEditMode();
    this.textarea.focus();
  }

  async onSlotChange(e) {
    const slottedTextarea = e.target.assignedElements({ flatten: true })[0];

    if (slottedTextarea) {
      this.selfTextarea.remove();
      this.textareaClone.remove();
      slottedTextarea.placeholder = this.t('placeholder');

      this.slottedTextarea = slottedTextarea;
    }
  }

  // Focus textarea on desktop only. We don't want to focus on mobile as the keyboard will show up
  focusTextareaOnDesktop() {
    !this.isMobileDevice && this.textarea.focus();
  }

  renderFiles() {
    return html`${this.files.map(this.renderFile.bind(this))}`;
  }

  renderFile(file, index) {
    return html`
      <section class="file-preview">
        <button
          data-index=${index}
          @click=${this.onRemoveFile}
          type="button"
          class="remove-file-button">
          <ef-icon name=${icons.form.remove_file}></ef-icon>
        </button>
        <span>${file.name}</span>
      </section>
    `;
  }

  renderExitEditModeButton() {
    return html`
      <button class="exit-btn" @click=${this.exitEditMode}>
        ${this.t('exitEditMode')}
        <ef-icon name=${icons.form.exit_edit_mode}></ef-icon>
      </button>
    `;
  }

  render() {
    return html`
      <form
        @submit=${this.onSubmit}
        class=${classMap({ 'has-files': this.hasFiles })}>
        ${this.isEdit ? this.renderExitEditModeButton() : null}
        <div class="left ${this.isEdit ? 'edit' : ''}">
          <section>
            <input
              @input=${this.onFileInput}
              id="file_upload"
              name="file"
              type="file"
              aria-label="upload"
              accept=${ifDefined(this.allowedFiles)} />
            <label class="file" for="file_upload">
              <ef-icon name=${icons.form.attachment}></ef-icon>
            </label>
            <div
              class="textarea-wrapper"
              @input=${this.onTextInput}
              @keydown=${this.onTextareaKeyDown}>
              <slot name="textarea" @slotchange=${this.onSlotChange}></slot>
              <textarea
                rows="1"
                placeholder=${this.t('placeholder')}></textarea>
            </div>
          </section>
          ${this.hasFiles ? this.renderFiles() : null}
        </div>
        <button type="submit" ?disabled=${!this.canSubmit} aria-label="submit">
          <ef-icon name=${icons.form.send}></ef-icon>
        </button>
      </form>
    `;
  }
}

ChatForm.registerTagName('ef-chat-form');

customElements.get('ef-chat-form') ||
  customElements.define('ef-chat-form', ChatForm);
